import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { UserProfile, NotificationPreferences } from '../types';

interface ProfileSectionProps {
  profile: UserProfile;
  onUpdate: (profile: Partial<UserProfile>) => void;
}

const ProfileSection = ({ profile, onUpdate }: ProfileSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(profile);
  const [notifications, setNotifications] = useState<NotificationPreferences>(
    profile.notificationPreferences
  );

  const roleOptions = [
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'analyst', label: 'Analista' },
    { value: 'viewer', label: 'Visualizador' }
  ];

  const departmentOptions = [
    { value: 'tax', label: 'Tributário' },
    { value: 'finance', label: 'Financeiro' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'operations', label: 'Operações' }
  ];

  const handleSave = () => {
    onUpdate({
      ...formData,
      notificationPreferences: notifications
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(profile);
    setNotifications(profile.notificationPreferences);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Perfil do Usuário</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie suas informações pessoais e preferências de conta
          </p>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Editar Perfil
          </Button>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start gap-6 mb-6">
          <div className="relative">
            <Image
              src={profile.avatar}
              alt={`Foto de perfil de ${profile.name}, ${profile.role} do departamento ${profile.department}`}
              className="w-24 h-24 rounded-full object-cover"
            />
            {isEditing && (
              <button
                className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full shadow-md hover:bg-primary/90 transition-colors"
                aria-label="Alterar foto"
              >
                <Icon name="Camera" size={16} color="currentColor" />
              </button>
            )}
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome Completo"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                required
              />

              <Input
                label="E-mail Corporativo"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!isEditing}
                required
              />

              <Input
                label="Telefone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!isEditing}
                placeholder="(11) 98765-4321"
              />

              <Select
                label="Função"
                options={roleOptions}
                value={formData.role}
                onChange={(value) => setFormData({ ...formData, role: value as UserProfile['role'] })}
                disabled={!isEditing || profile.role !== 'admin'}
              />

              <Select
                label="Departamento"
                options={departmentOptions}
                value={formData.department}
                onChange={(value) => setFormData({ ...formData, department: value as string })}
                disabled={!isEditing}
              />

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Último Acesso
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
                  <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-foreground">
                    {new Date(profile.lastLogin).toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Preferências de Notificação
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Notificações por E-mail"
              description="Receber alertas e atualizações por e-mail"
              checked={notifications.emailNotifications}
              onChange={(e) =>
                setNotifications({ ...notifications, emailNotifications: e.target.checked })
              }
              disabled={!isEditing}
            />

            <Checkbox
              label="Notificações por SMS"
              description="Receber alertas críticos por SMS"
              checked={notifications.smsNotifications}
              onChange={(e) =>
                setNotifications({ ...notifications, smsNotifications: e.target.checked })
              }
              disabled={!isEditing}
            />

            <Checkbox
              label="Alertas de Consulta"
              description="Notificar quando consultas forem concluídas"
              checked={notifications.consultationAlerts}
              onChange={(e) =>
                setNotifications({ ...notifications, consultationAlerts: e.target.checked })
              }
              disabled={!isEditing}
            />

            <Checkbox
              label="Atualizações do Sistema"
              description="Receber informações sobre novas funcionalidades"
              checked={notifications.systemUpdates}
              onChange={(e) =>
                setNotifications({ ...notifications, systemUpdates: e.target.checked })
              }
              disabled={!isEditing}
            />

            <Checkbox
              label="Relatórios Semanais"
              description="Resumo semanal de atividades e métricas"
              checked={notifications.weeklyReports}
              onChange={(e) =>
                setNotifications({ ...notifications, weeklyReports: e.target.checked })
              }
              disabled={!isEditing}
            />

            <Checkbox
              label="Alertas de Erro de API"
              description="Notificar sobre falhas de integração"
              checked={notifications.apiErrorAlerts}
              onChange={(e) =>
                setNotifications({ ...notifications, apiErrorAlerts: e.target.checked })
              }
              disabled={!isEditing}
            />
          </div>
        </div>

        {isEditing && (
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
              Salvar Alterações
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Segurança da Conta</h3>
        <div className="space-y-4">
          <Button variant="outline" iconName="Key" iconPosition="left" fullWidth>
            Alterar Senha
          </Button>
          <Button variant="outline" iconName="Shield" iconPosition="left" fullWidth>
            Configurar Autenticação de Dois Fatores
          </Button>
          <Button variant="outline" iconName="Smartphone" iconPosition="left" fullWidth>
            Gerenciar Sessões Ativas
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;